import type { ReactNode } from 'react'
import { SecondaryButton } from '../../../../../../components/execution/SecondaryButton'
import { TertiaryButton } from '../../../../../../components/execution/TertiaryButton'
import { LeafRank } from '../../../../../../components/know-why/LeafRank'
import { PowerFormBadge } from '../../../../../../components/ui-components/PowerFormBadge'
import { RegularTag } from '../../../../../../components/ui-components/RegularTag'
import { mfLogoBaseUrl } from '../../../../../../env'
import { lightImpactVibrate } from '../../../../../../utils/appBridge'
import { formatFundCategoryLabel, formatFundNatureLabel } from '../../../../../../utils/fundKindLabel'
import { LumpsumRecommendationStatus } from '../../api/common.types'
import type { SipRecommendationStatus } from '../../../sip/api/common.types'
import { PlanFundRecommendationStatusTag } from './PlanFundRecommendationStatusTag'
import { mapRatingToPowerForm } from '../../../utils/mapRatingToPowerFormUtils'

export type PlanFundRecommendationStatus = LumpsumRecommendationStatus | SipRecommendationStatus

type FundIdentity = {
  isin: string
  logoUrl?: string
  title: string
  fundNature?: string | null
  category?: string | null
  purchaseMode?: string | null
}

export type PlanFundCardStatus = {
  rank: number
  rating: string
}

export type PlanFundCardCta = {
  id: string
  label: string
  onClick: () => void
  variant: 'secondary' | 'tertiary' | 'completed'
}

export enum PlanFundInsightVisual {
  POSITIVE = 'POSITIVE',
  ALLOCATION = 'ALLOCATION',
  REBALANCE = 'REBALANCE',
  WARNING = 'WARNING',
  INFO = 'INFO'
}

export type PlanFundCardInsight = {
  id: string
  text: string
  visual: PlanFundInsightVisual
}

export type PlanFundCardBaseProps = {
  fund: FundIdentity
  topRight?: ReactNode
  status: PlanFundCardStatus
  badgeRowExtras?: ReactNode
  ctas?: PlanFundCardCta[]
  insights?: PlanFundCardInsight[]
  supportingText?: string
  showBottomDivider?: boolean
  badgeRowClassName?: string
  ctaRowClassName?: string
}

export type PlanFundCardProps = PlanFundCardBaseProps & {
  onCardClick?: () => void
  className?: string
  recommendationStatus?: PlanFundRecommendationStatus | null
  recommendationStatusDisplayText?: string | null
}

export function PlansFundCard({
  recommendationStatus,
  recommendationStatusDisplayText,
  onCardClick,
  className,
  ...baseProps
}: PlanFundCardProps) {
  const showStatusTag = shouldShowPlanFundRecommendationStatusTag(recommendationStatus)

  function handleCardClick() {
    if (!onCardClick) {
      return
    }
    onCardClick()
  }

  return (
    <div
      className={`flex flex-col bg-white ${onCardClick ? 'cursor-pointer' : ''} ${className ?? ''}`}
      onClick={onCardClick ? handleCardClick : undefined}
    >
      {showStatusTag ? (
        <div className="pl-[68px] pb-2">
          <PlanFundRecommendationStatusTag
            recommendationStatus={recommendationStatus}
            recommendationStatusDisplayText={recommendationStatusDisplayText ?? ''}
          />
        </div>
      ) : null}
      <PlansFundCardBase {...baseProps} />
    </div>
  )
}

export function PlansFundCardBase({
  fund,
  topRight,
  status,
  badgeRowExtras,
  ctas = [],
  insights = [],
  supportingText = '',
  showBottomDivider = true,
  badgeRowClassName,
  ctaRowClassName
}: PlanFundCardBaseProps) {
  const powerFormFromRating = mapRatingToPowerForm(status.rating)
  const showRankBadge = status.rank > 0 && powerFormFromRating != null
  const isRegularPurchaseMode = (fund.purchaseMode ?? '').toUpperCase() === 'REGULAR'
  const showBadgeRow =
    showRankBadge || powerFormFromRating != null || isRegularPurchaseMode || badgeRowExtras

  return (
    <div className="bg-white !min-h-[90px]">
      <div className="flex gap-3 items-start pl-4">
        <FundLogo fund={fund} />
        <div className={`flex-1 min-w-0 pr-5 ${showBottomDivider ? 'border-b border-[#F0F4F7] pb-5' : ''}`}>
          <div className="flex justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-[400] leading-[140%] text-[#232323] line-clamp-2">{fund.title}</div>
              <FundKindSubtitle fundNature={fund.fundNature} category={fund.category} />
              {showBadgeRow ? (
                <div className={`pt-3 flex flex-wrap items-center gap-x-2 gap-y-2 ${badgeRowClassName ?? ''}`}>
                  {showRankBadge ? <LeafRank isElite powerRank={status.rank} powerForm={powerFormFromRating} /> : null}
                  {powerFormFromRating ? <PowerFormBadge powerForm={powerFormFromRating} /> : null}
                  {isRegularPurchaseMode ? <RegularTag /> : null}
                  {badgeRowExtras}
                </div>
              ) : null}
            </div>
            {topRight}
          </div>

          {insights.length > 0 ? (
            <div className="pt-4 flex flex-col gap-2 pl-[2px]">
              {insights.map((insight) => (
                <FundInsightRow key={insight.id} insight={insight} />
              ))}
            </div>
          ) : null}

          {supportingText.length > 0 ? (
            <div className="pt-4 text-[13px] leading-[150%] text-[#6A6E6E]">{supportingText}</div>
          ) : null}

          {ctas.length > 0 ? (
            <div className={`pt-5 flex gap-3 ${ctaRowClassName ?? ''}`}>
              {ctas.map((cta) => (
                <PlanFundCardButton key={cta.id} cta={cta} />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export function PlanFundCardButton({ cta }: { cta: PlanFundCardCta }) {
  const handleClick = () => {
    lightImpactVibrate()
    cta.onClick()
  }

  if (cta.variant === 'secondary' || cta.variant === 'completed') {
    return (
      <div className="flex flex-1 min-w-0" onClick={(event) => event.stopPropagation()}>
        <SecondaryButton
          onClick={handleClick}
          label={cta.label}
          secondaryButtonClassName={
            cta.variant === 'completed'
              ? '!h-[40px] !max-w-full !min-w-[106px] !pb-[10px] !flex !flex-1 items-center justify-center !text-[13px] !bg-[#EFF9F0] !border-0 !text-[#2A9C4A]'
              : '!h-[40px] !max-w-full !min-w-[106px] !pb-[10px] !flex !flex-1 items-center justify-center !text-[13px]'
          }
        />
      </div>
    )
  }

  return (
    <div className="flex flex-1 min-w-0" onClick={(event) => event.stopPropagation()}>
      <TertiaryButton
        onClick={handleClick}
        label={cta.label}
        tertiaryButtonClassName="!h-[40px] !max-w-full !min-w-[106px] !pb-[10px] !flex !flex-1 items-center justify-center !text-[13px]"
      />
    </div>
  )
}

export function FundLogo({ fund }: { fund: FundIdentity }) {
  const logoSrc = fund.logoUrl?.length ? `${mfLogoBaseUrl}${fund.logoUrl}` : ''

  return (
    <div className="h-10 w-10 shrink-0 rounded-full bg-[#F0F4F7] overflow-hidden flex items-center justify-center">
      {logoSrc ? (
        <img
          className="h-full w-full object-contain"
          src={logoSrc}
          alt=""
          onError={(event) => {
            event.currentTarget.style.display = 'none'
          }}
        />
      ) : null}
    </div>
  )
}

export function FundInsightRow({ insight }: { insight: PlanFundCardInsight }) {
  const { textColor: insightTextColor, indicatorColor } = getPlanFundInsightColors(insight.visual)

  return (
    <div className="flex items-center gap-2">
      <div style={{ backgroundColor: indicatorColor }} className="h-[16px] w-[3px] rounded-full flex-shrink-0" />
      <span style={{ color: insightTextColor }} className="text-[13px] font-[500] leading-[150%] truncate">
        {insight.text}
      </span>
    </div>
  )
}

export function getPlanFundInsightColors(visual: PlanFundInsightVisual): {
  textColor: string
  indicatorColor: string
} {
  if (visual === PlanFundInsightVisual.POSITIVE) {
    return { textColor: '#00661D', indicatorColor: 'rgba(42, 156, 74, 0.3)' }
  }
  if (visual === PlanFundInsightVisual.INFO) {
    return { textColor: '#1470CC', indicatorColor: 'rgba(20, 112, 204, 0.3)' }
  }
  return { textColor: '#B85230', indicatorColor: 'rgba(184, 82, 48, 0.3)' }
}

export function shouldShowPlanFundRecommendationStatusTag(
  recommendationStatus?: PlanFundRecommendationStatus | null
): boolean {
  return (
    recommendationStatus === 'NEW' ||
    recommendationStatus === 'UPDATED' ||
    recommendationStatus === 'SAME'
  )
}

export function FundKindSubtitle({
  fundNature,
  category
}: {
  fundNature?: string | null
  category?: string | null
}) {
  const hasVisibleDetail = [fundNature, category].some((datum) => (datum ?? '').trim().length > 0)
  if (!hasVisibleDetail) {
    return null
  }

  const formattedDetails = [fundNature, category].flatMap((datum, detailIndex) => {
    if ((datum ?? '').trim().length === 0) {
      return []
    }
    const isCategory = detailIndex === 1
    const formatted = isCategory ? formatFundCategoryLabel(datum) : formatFundNatureLabel(datum)
    return [formatted]
  })

  return (
    <div className="pt-0.5 text-[13px] text-[#6A6E6E] font-[400]">
      {formattedDetails.join(' / ')}
    </div>
  )
}
